function isPageHeroSection(root) {
  return root.matches('.vecharge-page-hero') && Boolean(root.closest('.section-vecharge-page-hero'));
}

function getRevealTarget(root) {
  if (isPageHeroSection(root)) {
    return null;
  }

  if (root.matches('.vecharge-page-hero')) {
    return root.querySelector('.vecharge-page-hero__content');
  }

  if (root.matches('.vecharge-power-banner')) {
    return root.querySelector('.vecharge-power-banner__layout');
  }

  if (root.matches('.vecharge-app-showcase')) {
    return root.querySelector('.vecharge-app-showcase__grid');
  }

  if (root.matches('.vecharge-host-cta')) {
    return root.querySelector('.vecharge-host-cta__content');
  }

  if (root.matches('.vecharge-driver-features')) {
    return null;
  }

  if (root.matches('.vecharge-custom-footer')) {
    return root.querySelector(':scope > .page-width');
  }

  return root.querySelector(':scope > .page-width');
}

function getRevealElements(rootEl = document) {
  const elements = [];

  rootEl.querySelectorAll('section[class*="vecharge-"], footer.vecharge-custom-footer').forEach((root) => {
    if (root.classList.contains('vecharge-custom-header')) return;

    if (root.matches('.vecharge-driver-features')) {
      const header = root.querySelector('.vecharge-driver-features__header');
      const panel = root.querySelector('.vecharge-driver-features__panel');
      if (header) elements.push(header);
      if (panel) elements.push(panel);
      return;
    }

    const target = getRevealTarget(root);
    if (target) elements.push(target);
  });

  return elements;
}

function getPageHeroSections(rootEl = document) {
  return [...rootEl.querySelectorAll('.section-vecharge-page-hero .vecharge-page-hero')];
}

function getSplitCardFeatureLists(rootEl = document) {
  return [...rootEl.querySelectorAll('.vecharge-split-card__features')];
}

function isVechargeRevealInView(element) {
  const rect = element.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return rect.top < viewportHeight * 0.92 && rect.bottom > viewportHeight * 0.08;
}

function revealElement(element, instant = false) {
  const wasOffscreen = element.classList.contains('vecharge-scroll-reveal--offscreen');

  element.classList.remove('vecharge-scroll-reveal--offscreen');

  if (instant) {
    element.classList.add('vecharge-scroll-reveal--instant', 'vecharge-scroll-reveal--visible');
    return;
  }

  element.classList.remove('vecharge-scroll-reveal--instant');

  if (!wasOffscreen && element.classList.contains('vecharge-scroll-reveal--visible')) {
    return;
  }

  element.classList.add('vecharge-scroll-reveal--visible');
}

function hideElement(element) {
  element.classList.remove('vecharge-scroll-reveal--visible', 'vecharge-scroll-reveal--instant');
  element.classList.add('vecharge-scroll-reveal--offscreen');
}

const vechargeRevealObserver =
  typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              revealElement(entry.target, false);
            } else {
              hideElement(entry.target);
            }
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -8% 0px',
          threshold: 0.08,
        }
      )
    : null;

const splitCardFeaturesObserver =
  typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('vecharge-split-card__features--animate');
              entry.target.dataset.vechargeFeaturesPlayed = 'true';
            } else if (entry.target.dataset.vechargeFeaturesPlayed === 'true') {
              entry.target.classList.remove('vecharge-split-card__features--animate');
            }
          });
        },
        {
          root: null,
          rootMargin: '0px 0px -10% 0px',
          threshold: 0.2,
        }
      )
    : null;

function initializePageHeroSections(rootEl, reducedMotion) {
  getPageHeroSections(rootEl).forEach((section) => {
    if (section.dataset.vechargeHeroInit === 'true') return;

    section.dataset.vechargeHeroInit = 'true';
    section.classList.add('vecharge-page-hero--sequence');

    if (reducedMotion) {
      section.classList.add('vecharge-page-hero--no-motion');
    }
  });
}

function initializeSplitCardFeatures(rootEl, reducedMotion) {
  getSplitCardFeatureLists(rootEl).forEach((list) => {
    if (list.dataset.vechargeFeaturesInit === 'true') return;

    list.dataset.vechargeFeaturesInit = 'true';
    list.classList.add('vecharge-split-card__features--sequence');

    if (reducedMotion) {
      list.classList.add('vecharge-split-card__features--animate');
      return;
    }

    if (splitCardFeaturesObserver) {
      splitCardFeaturesObserver.observe(list);
    }

    if (isVechargeRevealInView(list)) {
      list.classList.add('vecharge-split-card__features--animate');
      list.dataset.vechargeFeaturesPlayed = 'true';
    }
  });
}

function prepareRevealElement(element) {
  element.classList.add('vecharge-scroll-reveal');

  if (element.dataset.vechargeRevealInit === 'true') return;

  element.dataset.vechargeRevealInit = 'true';
  hideElement(element);

  if (vechargeRevealObserver) {
    vechargeRevealObserver.observe(element);
  }
}

function initializeVechargeScrollReveal(rootEl = document) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  initializePageHeroSections(rootEl, reducedMotion);
  initializeSplitCardFeatures(rootEl, reducedMotion);

  const revealElements = getRevealElements(rootEl);

  if (reducedMotion) {
    revealElements.forEach((element) => {
      element.classList.add('vecharge-scroll-reveal', 'vecharge-scroll-reveal--instant', 'vecharge-scroll-reveal--visible');
      element.classList.remove('vecharge-scroll-reveal--offscreen');
    });
    document.documentElement.classList.add('vecharge-reveal-ready');
    return;
  }

  revealElements.forEach(prepareRevealElement);

  revealElements.forEach((element) => {
    if (isVechargeRevealInView(element)) {
      revealElement(element, false);
    }
  });

  document.documentElement.classList.add('vecharge-reveal-ready');
}

function initializeVechargeScrollRevealAll() {
  initializeVechargeScrollReveal(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVechargeScrollRevealAll, { once: true });
} else {
  initializeVechargeScrollRevealAll();
}

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.documentElement.classList.add('vecharge-reveal-ready');

  document.addEventListener('shopify:section:load', (event) => {
    initializeVechargeScrollReveal(event.target);
  });
}
